import { Point } from "./point";
import { Size } from "./size";
import { rename } from "fs";

/**
 * 矩形領域の左上隅の点と幅・高さを保持します。
 */
export class Rectangle {
  /**
   * 左上隅の点
   */
  public readonly topLeft: Point;
  /**
   * 幅
   */
  public /*readonly*/ width: number;
  /**
   * 高さ
   */
  public /*readonly*/ height: number;

  /**
   * @param rect 元となる矩形領域
   */
  constructor(rect: number[] | Rectangle);
  /**
   * @param topLeft 左上隅の点
   * @param size サイズ
   */
  constructor(topLeft: Point, size: Size | Point);
  /**
   * @param topLeft 左上隅の点
   * @param width 幅
   * @param height 高さ
   */
  constructor(topLeft: Point, width: number, height: number);
  /**
   * @param left 左辺の　x座標
   * @param top 上辺の y座標
   * @param right 右辺の　y座標
   * @param bottom 底辺の y座標
   */
  constructor(left: number, top: number, right: number, bottom: number);
  /**
   * 矩形領域情報を生成します。
   */
  constructor(
    arg0: any,
    arg1?: Size | Point | number,
    arg2?: number,
    arg3?: number,
  ) {
    let topLeft: Point, width: number, height: number;

    if (arg0 instanceof Rectangle) {
      const rect = arg0 as Rectangle;
      topLeft = rect.topLeft;
      width = rect.width;
      height = rect.height;
    } else if (arg0 instanceof Point && arg1 instanceof Size) {
      topLeft = new Point(arg0);
      width = (arg1 as Size).width;
      height = (arg1 as Size).height;
    } else if (arg0 instanceof Point && arg1 instanceof Point) {
      //topLeft,bottomRight
      topLeft = new Point(arg0);
      height = (arg1 as Point).y - topLeft.y;
      width = (arg1 as Point).x - topLeft.x;
    } else if (
      //topLeft,w,h
      arg0 instanceof Point &&
      typeof arg1 === "number" &&
      typeof arg2 === "number"
    ) {
      topLeft = new Point(arg0);
      width = arg1 as number;
      height = arg2 as number;
    } else if (Array.isArray(arg0)) {
      //[left,top,right,bottom]
      const l: number = arg0[0],
        t: number = arg0[1],
        r: number = arg0[2],
        b: number = arg0[3];
      topLeft = new Point(l, t);
      width = r - l;
      height = b - t;
    } else {
      //left,top,right,bottom
      const l = arg0 as number,
        t = arg1 as number,
        r = arg2 as number,
        b = arg3 as number;
      topLeft = new Point(l, t);
      width = r - l;
      height = b - t;
    }

    this.topLeft = topLeft;
    this.width = width;
    this.height = height;
  }

  /**
   * 右上隅の点を取得します。
   */
  public get topRight(): Point {
    return this.topLeft.offsetX(this.width);
  }

  /**
   * 左下隅の点を取得します。
   */
  public get bottomLeft(): Point {
    return this.topLeft.offset({ x: 0, y: this.height });
  }

  /**
   * 右下隅の点を取得します。
   */
  public get bottomRight(): Point {
    return this.topLeft.offset({ x: this.width, y: this.height });
  }

  /**
   * 中心点を取得します。
   */
  public get center(): Point {
    return this.topLeft.offset({
      x: this.width / 2,
      y: this.height / 2,
    });
  }

  /**
   * 左端の x座標 を取得します。
   */
  public get left(): number {
    return this.topLeft.x;
  }

  /**
   * 上面の y座標 を取得します。
   */
  public get top(): number {
    return this.topLeft.y;
  }

  /**
   * 右端の x座標 を取得します。
   */
  public get right(): number {
    return this.bottomRight.x;
  }

  /**
   * 底面の y座標 を取得します。
   */
  public get bottom(): number {
    return this.bottomRight.y;
  }

  /**
   * サイズ を取得します。
   */
  public get size(): Size {
    return new Size(this.width, this.height);
  }

  /**
   * 指定した点がこの矩形領域に含まれているかどうかを判断します。
   * @param p 検査対象の点
   */
  public contains(p: Point): boolean {
    return (
      this.left < p.x && p.x < this.right && this.top < p.y && p.y < this.bottom
    );
  }

  /**
   * @param x 縮小する幅
   * @param y 縮小する高さ
   */
  public deflate(x: number, y: number): Rectangle;
  /**
   * @param left 左辺を縮小する幅
   * @param top 上辺を縮小する幅
   * @param right 右辺を縮小する幅
   * @param bottom 底辺を縮小する幅
   */
  public deflate(
    left: number,
    top: number,
    right: number,
    bottom: number,
  ): Rectangle;
  /**
   * 指定された量だけ縮小した矩形領域のコピーを作成して返却します。
   * 元の矩形は変更されません。
   * @param args
   * @return 生成された新たな矩形領域
   */
  public deflate(...args: any[]): Rectangle {
    // by x,y
    if (
      args.length === 2 &&
      typeof args[0] === "number" &&
      typeof args[1] === "number"
    ) {
      const topLeft = this.topLeft.offset({ x: args[0], y: args[1] }),
        width = this.width - args[0] * 2,
        height = this.height - args[1] * 2;
      return new Rectangle(topLeft, width, height);
    } else {
      const left = args[0] as number,
        top = args[1] as number,
        right = args[2] as number,
        bottom = args[3] as number,
        topLeft = this.topLeft.offset({ x: left, y: top }),
        width = this.width - (left + right),
        height = this.height - (top + bottom);
      return new Rectangle(topLeft, width, height);
    }
  }

  /**
   * 指定された矩形領域と交差する領域が存在するかどうかを判断します。
   * @param other 検査対象の矩形領域
   */
  public intersectWith(other: Rectangle): boolean {
    return !(
      other.right <= this.left ||
      other.bottom <= this.top ||
      other.left >= this.right ||
      other.top >= this.bottom
    );
  }

  /**
   * 指定された矩形領域と交差するあ新たな矩形領域を返却します。
   * 元の矩形は変更されません。
   * 指定された矩形領域と交差しない場合は 元領域 と同様のものが返却されます。
   * Null rectを戻すように変更
   * @param other 検査対象の矩形領域
   */
  public intersection(other: Rectangle): Rectangle {
    if (!this.intersectWith(other)) {
      return new Rectangle(0, 0, 0, 0); //this -> 0,0,0,0
    }

    const l = Math.max(this.left, other.left),
      t = Math.max(this.top, other.top),
      r = Math.min(this.right, other.right),
      b = Math.min(this.bottom, other.bottom);
    return new Rectangle(l, t, r, b);
  }

  /**
   * 矩形情報の文字列理表現を返却します。
   */
  public toString(): string {
    let x = Math.round(this.topLeft.x * 10) / 10;
    let y = Math.round(this.topLeft.y * 10) / 10;
    let w = Math.round(this.width * 10) / 10;
    let h = Math.round(this.height * 10) / 10;
    return `[${x},${y},${w},${h}]`;
  }

  /**
   * 構造体の位置およびサイズが同じかどうかをテストします。
   * @param that 検査対象
   */
  public equals(that: Rectangle): boolean {
    return (
      this.topLeft.equals(that.topLeft) &&
      //this.width === that.width &&
      //this.height === that.height
      this.size.equals(that.size)
    );
  }

  //追加***********
  //Nullか？
  public isRectNull(): boolean {
    return (
      this.left == 0 && this.top == 0 && this.right == 0 && this.bottom == 0
    );
  }

  public isRectEmpty(): boolean {
    return this.width == 0 || this.height == 0;
  }

  public unionRectWith(rec: Rectangle): Rectangle {
    if (
      this.left == 0 &&
      this.top == 0 &&
      this.right == 0 &&
      this.bottom == 0
    ) {
      return new Rectangle(rec);
    }
    if (rec.left == 0 && rec.top == 0 && rec.right == 0 && rec.bottom == 0) {
      return new Rectangle(this);
    }
    let left = this.left < rec.left ? this.left : rec.left;
    let top = this.top < rec.top ? this.top : rec.top;
    let topLeft = new Point(left, top);
    let right = this.right < rec.right ? rec.right : this.right;
    let bottom = this.bottom < rec.bottom ? rec.bottom : this.bottom;
    return new Rectangle(topLeft, right - left, bottom - top);
  }

  public round(): void /*Rectangle*/ {
    let left = Math.round(this.topLeft.x);
    let top = Math.round(this.topLeft.y);
    let width = Math.round(this.width);
    let height = Math.round(this.height);
    //return	new Rectangle(new Point(left,top),width,height);
    this.topLeft.x = left;
    this.topLeft.y = top;
    this.width = width;
    this.height = height;
  }

  public ptInRect(pt: Point): boolean {
    if (pt.x < this.left) return false;
    if (pt.x >= this.right) return false;
    if (pt.y < this.top) return false;
    if (pt.y >= this.bottom) return false;
    return true;
  }
}
